import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { Gift, Coins, Sparkles, Heart } from 'lucide-react';

export const KidRewards: React.FC = () => {
    const provider = useProvider();
    const { session } = useAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [showWishDialog, setShowWishDialog] = React.useState(false);
    const [wishInput, setWishInput] = React.useState('');

    const { data: balance, isLoading: loadingBalance } = useQuery({
        queryKey: ['balance', session.user?.id],
        queryFn: () => provider.getBalance(session.user!.id),
        enabled: !!session.user?.id,
    });

    const { data: rewards, isLoading: loadingRewards } = useQuery({
        queryKey: ['rewards', session.user?.household_id],
        queryFn: () => provider.getRewards(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const { data: pendingRedemptions } = useQuery({
        queryKey: ['pendingRedemptions', session.user?.household_id],
        queryFn: () => provider.getPendingRedemptions(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const { data: kidWish } = useQuery({
        queryKey: ['kidWish', session.user?.id],
        queryFn: () => provider.getKidWish(session.user!.id),
        enabled: !!session.user?.id,
    });

    // Sort rewards by cost_points (lowest first)
    const sortedRewards = useMemo(() => {
        if (!rewards) return [];
        return [...rewards].sort((a, b) => a.cost_points - b.cost_points);
    }, [rewards]);

    const redeem = useMutation({
        mutationFn: (rewardId: string) => provider.redeemReward({
            reward_id: rewardId,
            kid_user_id: session.user!.id,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['balance'] });
            queryClient.invalidateQueries({ queryKey: ['pendingRedemptions'] });
            alert('Reward requested! Waiting for parent approval.');
        },
        onError: (err: any) => {
            alert(err.message || 'Check your points!');
        }
    });

    const setWish = useMutation({
        mutationFn: (wishTitle: string) => provider.setKidWish({
            kid_user_id: session.user!.id,
            household_id: session.user!.household_id,
            wish_title: wishTitle,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kidWish'] });
            setShowWishDialog(false);
            setWishInput('');
        },
        onError: (err: any) => {
            alert(err.message || 'Failed to save wish');
        }
    });

    if (loadingBalance || loadingRewards) return <div className="p-8 text-center font-bold text-purple-600">{t.common.loading}</div>;

    return (
    <>
        <div className="space-y-8 py-8">
            {/* Header */}
            <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-3">
                    <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                        <Sparkles className="text-amber-400" />
                        {t.kidRewards.title}
                    </h1>
                </div>
                <div className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-2 rounded-full font-black shadow-lg shadow-purple-200 uppercase">
                    <Coins size={20} className="text-amber-300" />
                    {balance} {t.kidRewards.availablePoints}
                </div>
            </div>

            {/* Wish Section */}
            <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-3xl p-6 border-2 border-pink-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-pink-100 rounded-2xl flex items-center justify-center">
                            <Heart className="text-pink-500" size={24} />
                        </div>
                        <div>
                            <h3 className="font-black text-slate-800 text-lg">{t.kidRewards.myWish}</h3>
                            {kidWish ? (
                                <p className="text-slate-600 font-medium">{t.kidRewards.workingTowards} {kidWish.wish_title}</p>
                            ) : (
                                <p className="text-slate-400 font-medium">{t.kidRewards.noWishSet}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setShowWishDialog(true);
                            setWishInput(kidWish?.wish_title || '');
                        }}
                        className="flex items-center gap-2 bg-pink-500 text-white px-4 py-2 rounded-2xl font-bold hover:bg-pink-600 transition-colors"
                    >
                        <Heart size={16} />
                        {kidWish ? t.kidRewards.changeWish : t.kidRewards.setWish}
                    </button>
                </div>
            </div>

            {/* Rewards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {sortedRewards?.length === 0 ? (
                    <div className="col-span-full bg-white rounded-3xl p-12 border-4 border-purple-50 text-center">
                        <Gift className="mx-auto text-purple-100 mb-4" size={64} />
                        <p className="text-slate-400 font-bold text-xl uppercase tracking-wider">{t.kidRewards.noRewards}</p>
                    </div>
                ) : (
                    sortedRewards?.map((reward) => {
                        const canAfford = (balance || 0) >= reward.cost_points;
                        const isPending = pendingRedemptions?.some(
                            redemption => redemption.reward_id === reward.id && redemption.kid_user_id === session.user!.id
                        );
                        return (
                            <div key={reward.id} className="bg-white rounded-3xl p-8 border-4 border-purple-50 flex flex-col items-center text-center shadow-sm hover:border-purple-200 transition-all">
                                <div className="w-20 h-20 bg-purple-50 rounded-2xl flex items-center justify-center text-purple-600 mb-6">
                                    <Gift size={40} />
                                </div>
                                <h3 className="text-2xl font-black text-slate-800 mb-2">{reward.title}</h3>
                                <div className="flex items-center gap-2 text-purple-600 font-bold text-3xl mb-8">
                                    <Coins size={24} className="text-amber-400" />
                                    {reward.cost_points}
                                </div>

                                <button
                                    disabled={!canAfford || redeem.isPending || isPending}
                                    onClick={() => redeem.mutate(reward.id)}
                                    className={`w-full py-4 rounded-2xl font-black text-xl transition-all shadow-md active:scale-95 uppercase ${
                                        isPending 
                                            ? 'bg-red-500 text-white cursor-not-allowed'
                                            : canAfford && !redeem.isPending
                                                ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                : 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                                    }`}
                                >
                                    {isPending ? t.kidRewards.alreadyPending : (redeem.isPending ? 'BUYING...' : (canAfford ? t.kidRewards.redeem : 'PREMALO TOČK'))}
                                </button>
                                {isPending ? (
                                    <p className="mt-4 text-xs text-amber-600 font-bold">{t.kidRewards.alreadyPending}</p>
                                ) : canAfford ? (
                                    <p className="mt-4 text-xs text-slate-400 font-bold">{t.kidRewards.redeemNotice}</p>
                                ) : (
                                    <p className="mt-4 text-xs text-amber-600 font-bold">
                                        Potrebuješ še {reward.cost_points - (balance || 0)} {t.common.pts || 'točk'}
                                    </p>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>

        {/* Wish Dialog */}
        {showWishDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-3xl p-6 max-w-md w-full">
                    <div className="flex items-center gap-3 mb-4">
                        <Heart className="text-purple-600" size={24} />
                        <h3 className="font-black text-slate-800 text-xl">{t.kidRewards.setWish}</h3>
                    </div>
                    <input
                        type="text"
                        value={wishInput}
                        onChange={(e) => setWishInput(e.target.value)}
                        placeholder={t.kidRewards.wishPlaceholder}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-2xl focus:border-pink-400 focus:outline-none font-medium"
                        autoFocus
                    />
                    <div className="flex gap-3 mt-4">
                        <button
                            onClick={() => setShowWishDialog(false)}
                            className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                            {t.common.cancel}
                        </button>
                        <button
                            onClick={() => setWish.mutate(wishInput)}
                            disabled={!wishInput.trim() || setWish.isPending}
                            className="flex-1 px-4 py-3 bg-pink-500 text-white rounded-2xl font-bold hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {setWish.isPending ? 'Saving...' : t.kidRewards.saveWish}
                        </button>
                    </div>
                </div>
            </div>
        )}
    </>
    );
};
